import { resolveFilesPublicPath } from './filesPublicPath';

const DEFAULT_INDEX = 9;
const DEFAULT_EXTENSION = 'svg';
const DEFAULT_COLOR = 'blue';
const DEFAULT_FILE = '09-blue.svg';

const KNOWN_COLORS = new Set([
  'black', 'blue', 'cyan', 'green', 'orange',
  'pink', 'purple', 'red', 'silver', 'yellow',
]);

const DEFAULT_COLOR_BY_THEME_MODE = {
  light: 'blue',
  dark: 'silver',
};

const DEFAULT_COLOR_BY_THEME_VARIANT = {
  'light/blue-goldenrod-01': 'blue',
  'dark/gray-goldenrod-01': 'silver'
};

const THEME_TOKEN_PATTERN = /^(light|dark|system|notfound)$/;
const THEME_VARIANT_PATTERN = /^(?!.*\/\/)(?!.*\.\.)[a-z0-9]+(?:[/-][a-z0-9]+)*$/;
const PRELOADER_FILE_PATTERN = /^(\d+)-([a-z0-9_-]+)\.([a-z0-9]+)$/i;
const SAFE_PRELOADER_FILE_PATTERN = /^[a-z0-9][a-z0-9_-]*\.(svg|gif|webp|png)$/i;

const preloaderAssets = (() => {
  const assets = {};

  try {
    if (typeof require !== 'undefined' && typeof require.context === 'function') {
      const context = require.context('@EPImages/preloaders', false, /\.(svg|gif|webp|png)$/i);
      context.keys().forEach((key) => {
        const filename = String(key || '').replace(/^\.\//, '').toLowerCase();
        const resolved = context(key);
        assets[filename] = resolved?.default || resolved;
      });
    }
  } catch (_) {
    return assets;
  }

  return assets;
})();

function normalizeThemeToken(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return THEME_TOKEN_PATTERN.test(normalized) ? normalized : null;
}

function normalizeThemeVariantName(value, fallback = '') {
  const normalized = String(value || '').trim().toLowerCase();
  if (!THEME_VARIANT_PATTERN.test(normalized)) return fallback;
  return normalized;
}

function normalizeColor(value, fallback = DEFAULT_COLOR) {
  const normalized = String(value || '').trim().toLowerCase();
  return KNOWN_COLORS.has(normalized) ? normalized : fallback;
}

function parsePreloaderFileSpec(file) {
  const normalized = String(file || '').trim().toLowerCase();
  const match = normalized.match(PRELOADER_FILE_PATTERN);
  if (!match) {
    if (SAFE_PRELOADER_FILE_PATTERN.test(normalized)) {
      return {
        index: DEFAULT_INDEX,
        color: DEFAULT_COLOR,
        extension: normalized.split('.').pop() || DEFAULT_EXTENSION,
        file: normalized,
      };
    }
    return null;
  }

  const index = Number.parseInt(match[1], 10);
  const color = normalizeColor(match[2], null);
  const extension = String(match[3] || '').trim().toLowerCase();
  if (!Number.isFinite(index) || index <= 0 || !color || !extension) return null;

  return { index, color, extension, file: normalized };
}

function normalizeLoaderSpec(raw, fallback) {
  const base = {
    index: Number(fallback?.index) || DEFAULT_INDEX,
    color: normalizeColor(fallback?.color, DEFAULT_COLOR),
    extension: String(fallback?.extension || DEFAULT_EXTENSION).trim().toLowerCase(),
    file: fallback?.file || null,
  };

  if (!raw) return base;

  if (typeof raw === 'string') {
    const parsed = parsePreloaderFileSpec(raw);
    if (parsed) return parsed;
    return {
      ...base,
      color: normalizeColor(raw, base.color),
    };
  }

  if (typeof raw !== 'object') return base;

  const parsedFromFile = raw.file ? parsePreloaderFileSpec(raw.file) : null;
  if (parsedFromFile) {
    return parsedFromFile;
  }

  const index = Number(raw.index ?? raw.preloaderIndex ?? base.index);
  const extension = String(raw.extension ?? raw.fileType ?? raw.fileExtension ?? base.extension).trim().toLowerCase();

  return {
    index: Number.isFinite(index) && index > 0 ? index : base.index,
    color: normalizeColor(raw.color, base.color),
    extension: extension || base.extension,
    file: null,
  };
}

function formatPreloaderFileName(index, color, extension) {
  const numericIndex = Number(index);
  const normalizedIndex = Number.isFinite(numericIndex) && numericIndex > 0
    ? String(numericIndex).padStart(2, '0')
    : String(DEFAULT_INDEX).padStart(2, '0');
  return `${normalizedIndex}-${normalizeColor(color, DEFAULT_COLOR)}.${String(extension || DEFAULT_EXTENSION).trim().toLowerCase()}`;
}

function resolveBundledPreloaderUrl(file) {
  const normalized = String(file || '').trim().toLowerCase().replace(/^\.\//, '');
  if (!normalized) return '';
  return preloaderAssets[normalized] || '';
}

function resolvePreloaderUrl(spec = {}, svgPath = globalSvgPath) {
  const formattedFile = formatPreloaderFileName(spec.index, spec.color, spec.extension);
  const fallbackColor = normalizeColor(spec.color, DEFAULT_COLOR);
  const candidates = [
    spec.file,
    formattedFile,
    `09-${fallbackColor}.svg`,
    DEFAULT_FILE,
    'wi-fi-blue.gif',
  ].filter(Boolean);

  for (const candidate of candidates) {
    const bundledUrl = resolveBundledPreloaderUrl(candidate);
    if (bundledUrl) return bundledUrl;
  }

  return `${svgPath || globalSvgPath}${formattedFile}`;
}

function resolveThemeConfig() {
  const cfg = window?.config?.theme;
  return cfg && typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
}

function resolveActiveThemeMode(themeConfig = {}) {
  const body = typeof document !== 'undefined' ? document.body : null;

  if (body?.classList?.contains('dark')) return 'dark';
  if (body?.classList?.contains('light')) return 'light';

  const configuredMode = normalizeThemeToken(themeConfig.mode);
  if (configuredMode === 'dark' || configuredMode === 'light') return configuredMode;

  return 'light';
}

function resolveThemeVariantNameByMode(mode, themeConfig = {}) {
  const lightVariant = normalizeThemeVariantName(themeConfig.light, 'light/blue-goldenrod-01');
  const darkVariant = normalizeThemeVariantName(themeConfig.dark, 'dark/gray-goldenrod-01');
  return mode === 'dark' ? darkVariant : lightVariant;
}

function resolveDefaultColor(mode, variantName) {
  const byVariant = DEFAULT_COLOR_BY_THEME_VARIANT[variantName];
  if (byVariant) return byVariant;
  return DEFAULT_COLOR_BY_THEME_MODE[mode] || DEFAULT_COLOR;
}

function resolveThemeLoaderSpec(overrides = {}) {
  const themeConfig = resolveThemeConfig();
  const mode = resolveActiveThemeMode(themeConfig);
  const variantName = resolveThemeVariantNameByMode(mode, themeConfig);
  const defaultColor = resolveDefaultColor(mode, variantName);

  const fallback = {
    index: Number(overrides.index) || DEFAULT_INDEX,
    color: normalizeColor(overrides.color, defaultColor),
    extension: String(overrides.extension || DEFAULT_EXTENSION).trim().toLowerCase(),
    file: overrides.file || null,
  };

  const loaderCfg = (themeConfig.loader && typeof themeConfig.loader === 'object' && !Array.isArray(themeConfig.loader))
    ? themeConfig.loader
    : (themeConfig.preloader && typeof themeConfig.preloader === 'object' && !Array.isArray(themeConfig.preloader))
      ? themeConfig.preloader
      : (themeConfig.preloaders && typeof themeConfig.preloaders === 'object' && !Array.isArray(themeConfig.preloaders))
        ? themeConfig.preloaders
        : {};

  let resolved = { ...fallback };
  const candidates = [
    loaderCfg?.[mode],
    loaderCfg?.[variantName],
    loaderCfg?.default,
    themeConfig?.[`${mode}Preloader`],
  ];

  candidates.forEach((candidate) => {
    if (candidate == null) return;
    resolved = normalizeLoaderSpec(candidate, resolved);
  });

  if (loaderCfg?.colors && typeof loaderCfg.colors === 'object') {
    resolved.color = normalizeColor(loaderCfg.colors[mode], resolved.color);
  }

  return resolved;
}

let globalSvgPath = resolveFilesPublicPath('@ProductImages/preloaders/');
const preloadImage = new Image();
const initialThemeSpec = resolveThemeLoaderSpec();
preloadImage.src = resolvePreloaderUrl(initialThemeSpec, globalSvgPath);

const activeLoaders = new Set();
let sharedLoaderRenderedAt = 0;

export function setLoaderSvgPath(path) {
  globalSvgPath = resolveFilesPublicPath(path);
  const themeSpec = resolveThemeLoaderSpec();
  preloadImage.src = resolvePreloaderUrl(themeSpec, globalSvgPath);
}

export default class Loader {
  constructor(index = DEFAULT_INDEX, color = null, text = null, options = {}) {
    let baseOptions = options || {};
    let resolvedIndex = index;
    let resolvedColor = color;
    let resolvedText = text;

    if (typeof index === 'object' && index !== null) {
      baseOptions = index;
      resolvedIndex = baseOptions.index ?? DEFAULT_INDEX;
      resolvedColor = baseOptions.color ?? null;
      resolvedText = baseOptions.text ?? null;
    }

    const requestedExtension = baseOptions.extension || baseOptions.fileType || baseOptions.fileExtension || DEFAULT_EXTENSION;
    const autoThemeAsset = resolvedColor == null;
    const themeSpec = resolveThemeLoaderSpec({
      index: resolvedIndex,
      color: resolvedColor,
      extension: requestedExtension,
    });

    this.index = Number(resolvedIndex ?? themeSpec.index ?? DEFAULT_INDEX) || DEFAULT_INDEX;
    this.color = normalizeColor(resolvedColor, themeSpec.color);
    this.fileExtension = String(requestedExtension || themeSpec.extension || DEFAULT_EXTENSION).trim().toLowerCase();
    this.text = resolvedText ?? window?.dic?.labels?.loading ?? 'loading';
    this.svgPath = baseOptions.svgPath || globalSvgPath;
    this.file = themeSpec.file || null;
    this.id = '__loader__';
    this.startTime = null;
    this.showTimeout = null;
    this.rendered = false;
    this._endedEarly = false;
    this._active = false;
    this._autoThemeAsset = autoThemeAsset;

    this.minDuration = 200;
    this.delayBeforeShow = 200;
    this.forceTimeout = null;

    if (baseOptions.minDuration != null) this.minDuration = baseOptions.minDuration;
    if (baseOptions.delayBeforeShow != null) this.delayBeforeShow = baseOptions.delayBeforeShow;
  }

  config(index = DEFAULT_INDEX, color = null, text = null, options = {}) {
    this.index = Number(index ?? this.index ?? DEFAULT_INDEX) || DEFAULT_INDEX;
    this.color = normalizeColor(color, this.color ?? DEFAULT_COLOR);
    this.text = text ?? window?.dic?.labels?.loading ?? this.text ?? 'loading';
    this.svgPath = options.svgPath || this.svgPath || globalSvgPath;
    this.fileExtension = options.extension || options.fileType || options.fileExtension || this.fileExtension || DEFAULT_EXTENSION;
    this.file = options.file || null;
    this._autoThemeAsset = color == null;
    if (options.minDuration != null) this.minDuration = options.minDuration;
    if (options.delayBeforeShow != null) this.delayBeforeShow = options.delayBeforeShow;
  }

  start() {
    this.clear();
    this._endedEarly = false;
    this.startTime = Date.now();
    this.rendered = false;
    this._active = true;
    activeLoaders.add(this);

    this.showTimeout = setTimeout(() => {
      if (this.rendered || this._endedEarly || !this._active) return;

      const existingOverlay = document.getElementById(this.id);
      if (existingOverlay) {
        this.rendered = true;
        return;
      }

      if (this._autoThemeAsset) {
        const themeSpec = resolveThemeLoaderSpec({
          index: this.index,
          color: this.color,
          extension: this.fileExtension,
        });
        this.index = themeSpec.index;
        this.color = themeSpec.color;
        this.fileExtension = themeSpec.extension;
        this.file = themeSpec.file || null;
      }

      const overlay = document.createElement('div');
      overlay.id = this.id;
      overlay.classList.add('overlay', 'app-loader');
      if (document.querySelector('.overlay:not(.app-loader)')) {
        overlay.classList.add('app-loader-stacked');
      }

      const img = document.createElement('img');
      img.hidden = true;
      img.src = resolvePreloaderUrl({
        index: this.index,
        color: this.color,
        extension: this.fileExtension,
        file: this.file,
      }, this.svgPath);
      img.alt = 'loading';
      img.addEventListener('load', () => {
        img.hidden = false;
      });
      img.addEventListener('error', () => {
        overlay.classList.add('app-loader-image-error');
        img.hidden = true;
      });
      overlay.append(img);
      document.body.appendChild(overlay);

      overlay.style.opacity = '1';
      sharedLoaderRenderedAt = Date.now();
      this.renderTime = sharedLoaderRenderedAt;
      this.rendered = true;
    }, this.delayBeforeShow);

    this.forceTimeout = setTimeout(() => this.end(true), 10000);
  }

  end(force = false) {
    if (this._endedEarly && !this._active) return;

    this._endedEarly = true;
    clearTimeout(this.showTimeout);
    clearTimeout(this.forceTimeout);

    if (this._active) {
      this._active = false;
      activeLoaders.delete(this);
    }

    if (activeLoaders.size > 0) {
      return;
    }

    const overlay = document.getElementById(this.id);
    if (!overlay) {
      if (activeLoaders.size === 0) sharedLoaderRenderedAt = 0;
      return;
    }

    const visibleTime = Date.now() - (
      sharedLoaderRenderedAt
      || this.renderTime
      || this.startTime
    );
    const delay = force ? 0 : Math.max(0, this.minDuration - visibleTime);

    setTimeout(() => {
      if (activeLoaders.size > 0) return;

      overlay.style.opacity = '0';
      if (overlay.parentNode) overlay.remove();
      if (activeLoaders.size === 0) sharedLoaderRenderedAt = 0;
    }, delay);
  }

  clear() {
    clearTimeout(this.showTimeout);
    clearTimeout(this.forceTimeout);

    if (this._active) {
      this._active = false;
      activeLoaders.delete(this);
    }

    if (activeLoaders.size > 0) return;

    const old = document.getElementById(this.id);
    if (old) old.remove();
    sharedLoaderRenderedAt = 0;
  }
}
